import { datadogLogs } from "@datadog/browser-logs";
import { ProgramUtilization, Tier } from "./billing";

export enum BackendType {
  ECS = "ecs",
  AWSVM = "aws",
  GCP = "gcp",
  GCPHosted = "gcpHosted",
}

export enum BackendTypeServer {
  ECS = "ecs",
  VM = "vm",
  AWS_HOSTED = "vm_aws",
  GCP_HOSTED = "vm_gcp",
  AZURE_HOSTED = "vm_azure",
}

// The types below are repetitive, yes,
// but I didn't want to run off and
// spend a lot of time defining type
// hierarchies when this works and
// to my eye is easy to read.
// If you don't like it, just pretend
// it was written in Go, and you'll
// feel much better :)
// (Just kidding, if you don't like it,
// we can change it)
export type ECSBackendOptions = {
  type: BackendType.ECS;
  credentials: AWSCredentials;
  region: string;
  network: NetworkConfig; // ECS only has Coiled-hosted
  registry: ContainerRegistry;
};

export type AWSVMBackendOptions = {
  type: BackendType.AWSVM;
  credentials: AWSCredentials;
  region: string;
  registry: ContainerRegistry;
  network: NetworkConfig;
};

export type GCPHostedOptions = {
  type: BackendType.GCPHosted;
  region: string;
  zone: string;
  registry: ContainerRegistry;
  network: NetworkConfig;
};

export type GCPBackendOptions = {
  type: BackendType.GCP;
  // TODO this should probably be project id right?
  projectId: string;
  region: string;
  zone: string;
  serviceCreds: Record<string, any>;
  instanceServiceAccount?: string;
  registry: ContainerRegistry;
  network: NetworkConfig;
};
export type ECSBackendOptionsReadOnly = Omit<
  ECSBackendOptions,
  "credentials" | "registry" | "network" // there's no good reason to omit the network, but for now it's not served up in the users/me route, so we can't have it here
> & {
  hasCredentials: boolean;
};

export type AWSVMBackendOptionsReadOnly = Omit<
  AWSVMBackendOptions,
  "credentials" | "registry"
> & {
  hasCredentials: boolean;
};

export type GCPHostedOptionsReadOnly = Omit<
  GCPHostedOptions,
  "registry" | "instance_service_account"
>;

export type GCPBackendOptionsReadOnly = Omit<
  GCPBackendOptions,
  "serviceCreds" | "registry"
> & {
  hasCredentials: boolean;
};
export type BackendOptionsReadOnly =
  | ECSBackendOptionsReadOnly
  | AWSVMBackendOptionsReadOnly
  | GCPHostedOptionsReadOnly
  | GCPBackendOptionsReadOnly;

export interface AccountBase {
  id: number;
  slug: string;
  avatarUrl?: string;
  name: string;
  coreLimit: number;
  creditLimit: number;
  earlyAdopter: boolean;
  active: boolean;
  membersLimit: number;
}

export type Account = AccountBase & {
  // Note that the django model
  // has a .backend property separate
  // from the backend options. To
  // save a lot of boilerplate in
  // typescript (i.e. if backend === blah && options === bleh)
  // I'm just going to put that as a property of "options", since in
  // reality they're codependent.
  options: BackendOptionsReadOnly;
  environmentVariables: Record<string, string>;
  registry: ContainerRegistryReadOnly;
  activeProgram: ActiveProgramReadOnly | ActiveProgramReadOnlyV2;
  configuredCustomerHosted: boolean;
  canUseCoiledHosted: boolean;
};

export enum AUTH_TYPE {
  ROLE_AUTH = "role",
  KEY_AUTH = "key",
}

export type AWSCredentials = {
  type: AUTH_TYPE;
  accessKeyId?: string;
  secretAccessKey?: string;
  roleArn?: string;
  externalId?: string;
};

export type GCPServiceAccount = {
  type?: string;
  clientId?: string;
  projectId?: string;
  privateKeyId?: string;
  privateKey?: string;
  authUri?: string;
  tokenUri?: string;
  clientEmail?: string;
  authProviderX509CertUrl?: string;
  clientX509CertUrl?: string;
};
export type GCPCredentials = {
  credentials?: GCPServiceAccount;
  instanceServiceAccount?: string;
};

export enum ContainerRegistryType {
  ECR = "ecr",
  DOCKER_HUB = "docker_hub",
  GAR = "gar",
}
export type ECRRegistry = {
  type: ContainerRegistryType.ECR;
  region: string;
  credentials: AWSCredentials;
};

export type ECRRegistryReadOnly = Omit<ECRRegistry, "credentials"> & {
  hasCredentials: boolean;
};

export type DockerHubRegistry = {
  type: ContainerRegistryType.DOCKER_HUB;
  uri: string;
  username: string;
  account: string | undefined;
  accessToken: string;
};

export type DockerHubRegistryReadOnly = Omit<DockerHubRegistry, "accessToken">;

export type GARRegistry = {
  type: ContainerRegistryType.GAR;
  location: string;
  projectId: string;
  serviceCreds: Record<string, any>;
};
// TODO capitalize
export type GarRegistryReadOnly = Omit<GARRegistry, "serviceCreds">;

export type ContainerRegistry = ECRRegistry | DockerHubRegistry | GARRegistry;

export type ContainerRegistryReadOnly =
  | ECRRegistryReadOnly
  | DockerHubRegistryReadOnly
  | GarRegistryReadOnly;

export type ActiveProgramReadOnly = ActiveProgramReadOnlyBase & {
  spent: number;
};

export type ActiveProgramReadOnlyV2 = ActiveProgramReadOnlyBase & {
  currentCredits: number;
  coreLimit: number;
  coreLimitMembers: number;
  nodeLimit: number;
  membersLimit: number;
  gpusLimit: number;
  creditAllotment: number;
};

type ActiveProgramReadOnlyBase = {
  hasQuota: boolean;
  quota: number;
  usagePercent: number;
  startDate: Date;
  endDate: Date;
  tier: Tier;
  utilizationLevel: ProgramUtilization;
  utilizationLevelUser: ProgramUtilization;
  spendLimitLevel: ProgramUtilization;
  coiledCreditValue: number;
  monthlySpent: number;
  creditsSpent: number;
  spendLimit: number | null;
  spendLimitPercentage: number;
  spendLimitShouldStopInstances: boolean;
};

export const getDefaultECRValues = (
  registry?: ECRRegistryReadOnly,
): ECRRegistry => {
  return {
    type: ContainerRegistryType.ECR,
    region: (registry && registry.region) || "",
    credentials: {
      type: AUTH_TYPE.KEY_AUTH,
      accessKeyId: "",
      secretAccessKey: "",
    },
  };
};

export const getDefaultDockerHubValues = (
  registry?: DockerHubRegistryReadOnly,
): DockerHubRegistry => ({
  type: ContainerRegistryType.DOCKER_HUB,
  uri: (registry && registry.uri) || "docker.io",
  username: (registry && registry.username) || "",
  account: (registry && registry.account) || "",
  accessToken: "",
});

export const getDefaultGARValues = (
  registry?: GarRegistryReadOnly,
): GARRegistry => ({
  type: ContainerRegistryType.GAR,
  location: (registry && registry.location) || "",
  projectId: (registry && registry.projectId) || "",
  serviceCreds: {},
});

export const getDefaultContainerRegistryValues = (
  registry?: ContainerRegistryReadOnly,
): ContainerRegistry => {
  if (!registry) {
    return getDefaultECRValues();
  }
  if (registry.type === ContainerRegistryType.ECR) {
    return getDefaultECRValues(registry);
  } else if (registry.type === ContainerRegistryType.GAR) {
    return getDefaultGARValues(registry);
  } else if (registry.type === ContainerRegistryType.DOCKER_HUB) {
    return getDefaultDockerHubValues(registry);
  } else {
    datadogLogs.logger.error("Unexpected registry type", {
      registryType: (registry as any).type,
    });
    return getDefaultECRValues();
  }
};

export enum NetworkConfigType {
  COILED_CREATES = "coiled_creates",
  BYO = "byo",
}

export type ByoNetworkConfig = {
  networkId: string;
  schedulerSubnetId: string;
  workerSubnetId: string;
  firewallId: string;
  schedulerFirewallId: string;
  workerPublicIps: boolean;
};

export type NetworkConfig = {
  type: NetworkConfigType.BYO | NetworkConfigType.COILED_CREATES;
  networkConfig?: ByoNetworkConfig;
};

export const COILED_ACCOUNT_SLUG = "examples";
